import CustomAlertDialog from "@/components/CustomAlertDialog";
import InterviewDialog from "@/components/formDialog/InterviewDialog";
import Spinner from "@/components/Spinner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useApplicationInterviewsQuery,
  useUpdateInterviewMutation,
} from "@/hooks/api/interview.queries";
import usePermissions from "@/hooks/usePermissions";
import {
  InterviewStatusValue,
  InterviewType,
} from "@/services/interview.service";
import { formatDate } from "@/utils/formatDate.util";
import {
  CalendarClock,
  CalendarPlus,
  CheckCircle2,
  Link2,
  MapPin,
  Star,
  UserX,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import InterviewOutcomeDialog from "./InterviewOutcomeDialog";

interface InterviewManageDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  applicationId: string;
  internshipId: string;
  studentName?: string;
}

const statusConfig: Record<
  InterviewStatusValue,
  { label: string; className: string }
> = {
  scheduled: {
    label: "Scheduled",
    className: "bg-blue-100 text-blue-700 border-blue-200",
  },
  completed: {
    label: "Completed",
    className: "bg-green-100 text-green-700 border-green-200",
  },
  no_show: {
    label: "No Show",
    className: "bg-orange-100 text-orange-700 border-orange-200",
  },
  cancelled: {
    label: "Cancelled",
    className: "bg-red-100 text-red-700 border-red-200",
  },
};

const typeLabels: Record<string, string> = {
  phone: "Phone",
  video: "Video",
  in_person: "In Person",
  technical: "Technical",
  hr: "HR",
};

const InterviewManageDialog = ({
  isOpen,
  onOpenChange,
  applicationId,
  internshipId,
  studentName,
}: InterviewManageDialogProps) => {
  const { can } = usePermissions();
  const canSchedule = can("interview.schedule");
  const canUpdate = can("interview.update");

  const {
    data: interviews = [],
    isLoading,
    isError,
    refetch,
  } = useApplicationInterviewsQuery(applicationId, isOpen);

  const cancelMutation = useUpdateInterviewMutation(internshipId, applicationId);

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [reschedulingInterview, setReschedulingInterview] =
    useState<InterviewType | null>(null);
  const [outcomeTarget, setOutcomeTarget] = useState<{
    interview: InterviewType;
    outcome: "completed" | "no_show" | "edit";
  } | null>(null);
  const [cancelTarget, setCancelTarget] = useState<InterviewType | null>(null);

  const handleCancel = () => {
    if (!cancelTarget) return;
    cancelMutation.mutate({
      interviewId: cancelTarget.id,
      data: { status: "cancelled" },
      successMessage: "Interview cancelled",
      successDescription:
        "It has been removed from the student's upcoming list and a cancellation email was sent.",
    });
    setCancelTarget(null);
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Interview Rounds</DialogTitle>
            <DialogDescription>
              {studentName
                ? `All interview rounds for ${studentName}.`
                : "All interview rounds for this application."}{" "}
              Every change here syncs to the student app automatically.
            </DialogDescription>
          </DialogHeader>

          {canSchedule && (
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={() => {
                  setReschedulingInterview(null);
                  setScheduleOpen(true);
                }}
              >
                <CalendarPlus size={15} className="mr-1.5" />
                Schedule Round
              </Button>
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center gap-3 py-10">
              <p className="text-sm text-red-500">Failed to load interviews.</p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Retry
              </Button>
            </div>
          ) : interviews.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-50 text-gray-300">
                <CalendarClock size={24} />
              </div>
              <h4 className="text-sm font-semibold text-gray-900">
                No interviews scheduled
              </h4>
              <p className="mt-1 text-xs text-gray-500">
                {canSchedule
                  ? "Schedule the first round to notify the student."
                  : "You do not have permission to schedule interviews."}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {interviews.map((interview) => {
                const status =
                  statusConfig[interview.status] ?? statusConfig.scheduled;
                const isOpenRound = interview.status === "scheduled";

                return (
                  <div
                    key={interview.id}
                    className="rounded-xl border border-gray-100 p-4 shadow-sm"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-gray-900">
                            {typeLabels[interview.interview_type] ||
                              interview.interview_type}
                          </span>
                          <Badge
                            className={`border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${status.className}`}
                          >
                            {status.label}
                          </Badge>
                          {interview.rating ? (
                            <span className="flex items-center gap-0.5 text-xs font-bold text-yellow-600">
                              <Star
                                size={12}
                                className="fill-yellow-400 text-yellow-400"
                              />
                              {interview.rating}/5
                            </span>
                          ) : null}
                        </div>
                        <p className="mt-1 text-[13px] font-medium text-gray-700">
                          {formatDate(interview.scheduled_at, {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })}
                          <span className="text-gray-400">
                            {" · "}
                            {interview.duration_minutes} min
                          </span>
                        </p>
                        {interview.meeting_link && (
                          <a
                            href={interview.meeting_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-1 flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline"
                          >
                            <Link2 size={12} />
                            <span className="max-w-xs truncate">
                              {interview.meeting_link}
                            </span>
                          </a>
                        )}
                        {interview.venue && (
                          <p className="mt-1 flex items-center gap-1 text-xs text-gray-500">
                            <MapPin size={12} />
                            {interview.venue}
                          </p>
                        )}
                        {interview.feedback && (
                          <p className="mt-2 whitespace-pre-wrap rounded-lg bg-gray-50 p-2 text-xs text-gray-600">
                            {interview.feedback}
                          </p>
                        )}
                      </div>
                    </div>

                    {canUpdate && (
                      <div className="mt-3 flex flex-wrap justify-end gap-2 border-t border-gray-50 pt-3">
                        {isOpenRound ? (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-[11px] font-semibold"
                              onClick={() => {
                                setReschedulingInterview(interview);
                                setScheduleOpen(true);
                              }}
                            >
                              <CalendarClock size={13} className="mr-1" />
                              Reschedule
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 border-orange-200 px-2 text-[11px] font-semibold text-orange-700 hover:bg-orange-50"
                              onClick={() =>
                                setOutcomeTarget({
                                  interview,
                                  outcome: "no_show",
                                })
                              }
                            >
                              <UserX size={13} className="mr-1" />
                              No Show
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 border-red-200 px-2 text-[11px] font-semibold text-red-700 hover:bg-red-50"
                              onClick={() => setCancelTarget(interview)}
                            >
                              <XCircle size={13} className="mr-1" />
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              className="h-7 bg-green-600 px-2 text-[11px] font-semibold hover:bg-green-700"
                              onClick={() =>
                                setOutcomeTarget({
                                  interview,
                                  outcome: "completed",
                                })
                              }
                            >
                              <CheckCircle2 size={13} className="mr-1" />
                              Mark Completed
                            </Button>
                          </>
                        ) : interview.status !== "cancelled" ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-[11px] font-semibold"
                            onClick={() =>
                              setOutcomeTarget({ interview, outcome: "edit" })
                            }
                          >
                            <Star size={13} className="mr-1" />
                            Update Evaluation
                          </Button>
                        ) : null}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <InterviewDialog
        applicationId={applicationId}
        internshipId={internshipId}
        interview={reschedulingInterview}
        isOpen={scheduleOpen}
        onOpenChange={(open) => {
          setScheduleOpen(open);
          if (!open) setReschedulingInterview(null);
        }}
      />

      <InterviewOutcomeDialog
        isOpen={!!outcomeTarget}
        onClose={() => setOutcomeTarget(null)}
        interview={outcomeTarget?.interview ?? null}
        internshipId={internshipId}
        applicationId={applicationId}
        outcome={outcomeTarget?.outcome ?? "completed"}
      />

      <CustomAlertDialog
        isOpen={!!cancelTarget}
        onOpenChange={(open) => !open && setCancelTarget(null)}
        title="Cancel this interview?"
        description="It will disappear from the student's upcoming interviews and they will receive a cancellation email. This cannot be undone — you would need to schedule a new round."
        confirmText="Yes, Cancel Interview"
        cancelText="Keep It"
        onConfirm={handleCancel}
      />
    </>
  );
};

export default InterviewManageDialog;
